import { useState, useEffect } from "react";
import { useToast } from "@/components/base/Toast";
import { defaultInventoryFields, InventoryField } from "@/mocks/inventoryFieldsData";
import {
  SellingType,
  defaultSellingTypes,
  sellingTypeColorOptions,
  getSellingTypeColorClass,
} from "@/mocks/sellingTypesData";
import { fetchApi } from "@/services/apiService";

type Props = {
  sellingTypes: SellingType[];
  inventoryFields?: InventoryField[];
  onTypesChange: (types: SellingType[]) => void;
  onSave?: (types: SellingType[]) => Promise<void>;
};

const colorRingMap: Record<string, string> = {
  indigo: "ring-indigo-500",
  green: "ring-green-500",
  amber: "ring-amber-500",
  red: "ring-red-500",
  purple: "ring-purple-500",
  pink: "ring-pink-500",
  teal: "ring-teal-500",
  orange: "ring-orange-500",
};

const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export default function SellingTypesBuilder({
  sellingTypes: propTypes,
  inventoryFields: propFields = [],
  onTypesChange,
  onSave,
}: Props) {
  const { showToast } = useToast();
  const [types, setTypes] = useState<SellingType[]>(propTypes || []);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState<SellingType | null>(null);
  const [typeName, setTypeName] = useState("");
  const [typeColor, setTypeColor] = useState("indigo");
  const [selectedFieldIds, setSelectedFieldIds] = useState<(number | string)[]>([]);
  const [trackInventory, setTrackInventory] = useState(true);
  const [itemLabel, setItemLabel] = useState("Unit");
  const [identifierField, setIdentifierField] = useState("Unit Number");
  const [availableStatus, setAvailableStatus] = useState("Available");
  const [soldStatus, setSoldStatus] = useState("Sold");
  const [progressLabel, setProgressLabel] = useState("Units sold");

  const activeFields = propFields || [];

  useEffect(() => {
    setTypes(propTypes || []);
  }, [propTypes]);

  const updateTypes = (updated: SellingType[]) => {
    setTypes(updated);
    onTypesChange(updated);
    if (onSave) {
      onSave(updated);
    }
  };

  const handleDelete = async (id: string) => {
    const updated = types.filter((t) => t.id !== id);
    updateTypes(updated);
    try {
      await fetchApi(`/projects/settings/selling-types/${id}`, { method: "DELETE" });
    } catch {
      // Fallback
    }
    showToast("Selling type removed", "success");
  };

  const openAddModal = () => {
    setEditingType(null);
    setTypeName("");
    setTypeColor("indigo");
    setSelectedFieldIds(activeFields.slice(0, 4).map((f) => f.id));
    setTrackInventory(true);
    setItemLabel("Unit");
    setIdentifierField(activeFields[0]?.label || "Unit Number");
    setAvailableStatus("Available");
    setSoldStatus("Sold");
    setProgressLabel("Units sold");
    setShowModal(true);
  };

  const openEditModal = (st: SellingType) => {
    setEditingType(st);
    setTypeName(st.name);
    setTypeColor(st.color || "indigo");
    setSelectedFieldIds([...(st.inventoryFieldIds || [])]);
    setTrackInventory(st.trackInventory !== false);
    setItemLabel(st.itemLabel || "Unit");
    setIdentifierField(st.identifierField || activeFields[0]?.label || "Unit Number");
    setAvailableStatus(st.availableStatus || "Available");
    setSoldStatus(st.soldStatus || "Sold");
    setProgressLabel(st.progressLabel || "Units sold");
    setShowModal(true);
  };

  const toggleField = (id: number | string) => {
    setSelectedFieldIds((prev) =>
      prev.some((fid) => String(fid) === String(id))
        ? prev.filter((fid) => String(fid) !== String(id))
        : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedFieldIds(activeFields.map((f) => f.id));
  };

  const deselectAll = () => {
    setSelectedFieldIds([]);
  };

  const handleSave = () => {
    if (!typeName.trim()) {
      showToast("Please enter a type name", "error");
      return;
    }
    if (selectedFieldIds.length === 0) {
      showToast("Please select at least one inventory field", "error");
      return;
    }

    const firstField = activeFields.find((f) =>
      selectedFieldIds.some((fid) => String(fid) === String(f.id))
    );

    const commonFields = {
      trackInventory,
      itemLabel: itemLabel.trim() || "Unit",
      identifierField:
        identifierField ||
        firstField?.label ||
        "Unit Number",
      availableStatus: availableStatus.trim() || "Available",
      soldStatus: soldStatus.trim() || "Sold",
      progressLabel: progressLabel.trim() || "Units sold",
    };

    if (editingType) {
      const updated = types.map((t) =>
        t.id === editingType.id
          ? {
              ...t,
              name: typeName.trim(),
              color: typeColor,
              inventoryFieldIds: selectedFieldIds as any,
              ...commonFields,
            }
          : t
      );
      updateTypes(updated);
      showToast("Selling type updated", "success");
    } else {
      const newType: SellingType = {
        id: generateUUID(),
        name: typeName.trim(),
        color: typeColor,
        inventoryFieldIds: selectedFieldIds as any,
        ...commonFields,
      };
      updateTypes([...types, newType]);
      showToast("Selling type saved", "success");
    }
    setShowModal(false);
  };

  const getFieldName = (id: number | string): string => {
    const found = activeFields.find(
      (f) => String(f.id) === String(id) || f.label === id || f.fieldKey === id
    );
    return found?.label || `Field ${id}`;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">What You&apos;re Selling</h2>
          <p className="text-sm text-gray-500 mt-1">
            Create types for what you sell. Assign inventory fields to each type. These appear when creating a project.
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-all duration-200 active:scale-[0.98] whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line w-4 h-4 flex items-center justify-center" />+ Add Type
        </button>
      </div>

      {/* Types List */}
      <div>
        {types.map((st) => (
          <div
            key={st.id}
            className="bg-white rounded-xl border border-gray-200 p-4 mb-3 hover:border-indigo-300 transition-colors"
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              {/* Left: color dot + name + count */}
              <div className="flex items-center gap-2.5 min-w-0">
                <span className={`w-4 h-4 rounded-full flex-shrink-0 ${getSellingTypeColorClass(st.color)}`} />
                <span className="text-sm font-semibold text-gray-900 truncate">{st.name}</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 whitespace-nowrap">
                  {st.inventoryFieldIds?.length || 0} fields
                </span>
              </div>

              {/* Middle: field pills */}
              <div className="flex flex-wrap gap-1 flex-1 min-w-0">
                {st.inventoryFieldIds?.map((fid) => (
                  <span
                    key={String(fid)}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 whitespace-nowrap"
                  >
                    {getFieldName(fid)}
                  </span>
                ))}
              </div>

              {/* Right: actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => openEditModal(st)}
                  className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors cursor-pointer"
                  title="Edit Type"
                >
                  <i className="ri-pencil-line w-3.5 h-3.5 flex items-center justify-center" />
                </button>
                <button
                  onClick={() => handleDelete(st.id)}
                  className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  title="Delete Type"
                >
                  <i className="ri-delete-bin-line w-3.5 h-3.5 flex items-center justify-center" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {types.length === 0 && (
          <div className="py-8 text-center text-sm text-gray-400">
            No selling types created yet. Click &quot;+ Add Type&quot; above to create one.
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setShowModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">
                  {editingType ? "Edit Selling Type" : "Add Selling Type"}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line w-5 h-5 flex items-center justify-center" />
                </button>
              </div>

              <div className="p-5 space-y-5">
                {/* Type Name */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Type Name *</label>
                  <input
                    type="text"
                    value={typeName}
                    onChange={(e) => setTypeName(e.target.value)}
                    placeholder="e.g. Residential Property, Industrial Product, MBA Course"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all"
                  />
                </div>

                {/* Color Swatches */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Color</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {sellingTypeColorOptions.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setTypeColor(c)}
                        className={`w-7 h-7 rounded-full ${getSellingTypeColorClass(c)} cursor-pointer transition-all ${
                          typeColor === c ? `ring-2 ring-offset-2 ${colorRingMap[c] || "ring-indigo-500"}` : ""
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Field Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Which inventory fields does this type use?
                  </label>
                  <p className="text-xs text-gray-400 mb-3">Select from your configured inventory fields:</p>

                  <div className="flex items-center justify-end gap-3 mb-1">
                    <button
                      type="button"
                      onClick={selectAll}
                      className="text-xs text-indigo-600 hover:text-indigo-700 font-medium cursor-pointer whitespace-nowrap"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300 text-xs">/</span>
                    <button
                      type="button"
                      onClick={deselectAll}
                      className="text-xs text-indigo-600 hover:text-indigo-700 font-medium cursor-pointer whitespace-nowrap"
                    >
                      Deselect All
                    </button>
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                    {activeFields.map((field) => (
                      <label
                        key={String(field.id)}
                        className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedFieldIds.some((fid) => String(fid) === String(field.id))}
                          onChange={() => toggleField(field.id)}
                          className="w-4 h-4 rounded accent-indigo-600 cursor-pointer"
                        />
                        <span className="text-sm text-gray-900">{field.label}</span>
                        <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded ml-auto">{field.type}</span>
                      </label>
                    ))}
                  </div>

                  {/* Live Preview */}
                  <div className="bg-gray-50 rounded-xl p-3 mt-3">
                    <p className="text-xs text-gray-500 mb-2">Selected fields ({selectedFieldIds.length}):</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedFieldIds.length === 0 ? (
                        <span className="text-xs text-gray-400 italic">No fields selected</span>
                      ) : (
                        selectedFieldIds.map((fid) => (
                          <span
                            key={String(fid)}
                            className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs"
                          >
                            {getFieldName(fid)}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Inventory Tracking Section ── */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-4">Inventory Tracking</h4>

                  {/* Toggle */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-700">Track inventory for this type?</span>
                    <button
                      type="button"
                      onClick={() => setTrackInventory(!trackInventory)}
                      className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${trackInventory ? "bg-indigo-600" : "bg-gray-300"}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${trackInventory ? "translate-x-4" : "translate-x-0.5"}`} />
                    </button>
                  </div>

                  {trackInventory && (
                    <div className="space-y-4">
                      {/* Item Label */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Item Label</label>
                        <p className="text-xs text-gray-400 mb-2">What is one item called?</p>
                        <input
                          type="text"
                          value={itemLabel}
                          onChange={(e) => setItemLabel(e.target.value)}
                          placeholder="e.g. Unit, Seat, Product, Slot, Plan, Vehicle"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all"
                        />
                        <p className="text-xs text-gray-400 mt-1">Shows as &apos;48 {itemLabel || "Units"}&apos; on project card</p>
                      </div>

                      {/* Primary Identifier Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Primary Identifier Field</label>
                        <p className="text-xs text-gray-400 mb-2">Which field is the item&apos;s main name?</p>
                        <select
                          value={identifierField}
                          onChange={(e) => setIdentifierField(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 bg-white"
                        >
                          {activeFields.map((f) => (
                            <option key={String(f.id)} value={f.label}>{f.label}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-400 mt-1">Shown as item title in inventory table</p>
                      </div>

                      {/* Available Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Available Status Value</label>
                        <p className="text-xs text-gray-400 mb-2">Which status means Available?</p>
                        <input
                          type="text"
                          value={availableStatus}
                          onChange={(e) => setAvailableStatus(e.target.value)}
                          placeholder="e.g. Available, In Stock, Open, Active"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all"
                        />
                      </div>

                      {/* Sold/Booked Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sold/Booked Status Value</label>
                        <p className="text-xs text-gray-400 mb-2">Which status means Sold/Booked?</p>
                        <input
                          type="text"
                          value={soldStatus}
                          onChange={(e) => setSoldStatus(e.target.value)}
                          placeholder="e.g. Sold, Booked, Enrolled, Out of Stock"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all"
                        />
                      </div>

                      {/* Progress Bar Label */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Progress Bar Label</label>
                        <p className="text-xs text-gray-400 mb-2">Progress bar label on project card</p>
                        <input
                          type="text"
                          value={progressLabel}
                          onChange={(e) => setProgressLabel(e.target.value)}
                          placeholder="e.g. Units sold, Seats filled, Stock status"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  Save Type
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}